import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, AlertCircle, Sparkles } from 'lucide-react';
import { ProgressBar } from '@/components/ProgressBar';
import { TurnstileWidget } from '@/components/TurnstileWidget';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { LoadingOverlay } from '@/components/LoadingOverlay';
import { RateLimitErrorDisplay } from '@/components/RateLimitError';
import { storage } from '@/lib/storage';
import { api } from '@/lib/api';
import { RateLimitError, TurnstileError, ValidationError } from '@/lib/errors';
import { funnelAnalytics } from '@/services/funnel-analytics';

export default function EmailCapturePage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [consentMarketing, setConsentMarketing] = useState(true); // Default to checked
  const [turnstileToken, setTurnstileToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);

  // Check if running on localhost for development bypass
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';

  useEffect(() => {
    // Check if we have goals and answers
    const goals = storage.getGoals();
    const answers = storage.getAnswers();

    if (goals.length === 0 || Object.keys(answers).length === 0) {
      // Missing data - redirect back
      navigate('/goals');
      return;
    }

    // Track funnel step: Email viewed
    funnelAnalytics.trackEmailViewed();

    // Load saved email if any
    const savedEmail = storage.getEmail();
    if (savedEmail) {
      setEmail(savedEmail);
    }

    storage.setCurrentStep('email');

    // Auto-set dev bypass token for localhost
    if (isLocalhost) {
      setTurnstileToken('dev-bypass');
    }
  }, [navigate, isLocalhost]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    setError('');
    setRateLimitError(null);

    // Validation
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the security verification');
      return;
    }

    const goals = storage.getGoals();
    const answers = storage.getAnswers();

    try {
      setLoading(true);
      const response = await api.generateReport(
        goals,
        answers,
        email,
        consentMarketing,
        turnstileToken
      );

      // Track funnel step: Email submitted
      funnelAnalytics.trackEmailSubmitted(email);

      // Save to storage
      storage.setEmail(email, consentMarketing);
      storage.setUserId(response.userId);
      storage.setReport(response.report);
      storage.setReportId(response.report.id);
      storage.setCurrentStep('report');

      // Navigate to report
      navigate('/report');
    } catch (err) {
      console.error('Failed to generate report:', err);

      if (err instanceof RateLimitError) {
        setRateLimitError(err);
      } else if (err instanceof TurnstileError) {
        setError('Security verification failed. Please refresh the page and try again.');
      } else if (err instanceof ValidationError) {
        setError(err.message);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to generate report. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = email.trim() && validateEmail(email) && turnstileToken && !loading;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading overlay with fun messages for report generation */}
      <LoadingOverlay
        isLoading={loading}
        messages={[
          'Generating your plan...',
          'Analyzing priorities...',
          'Creating timeline...',
          'Crafting strategies...',
          'Building your roadmap...',
          'Adding finishing touches...',
          'Sending to your email...',
          'Almost done...',
        ]}
        rotationInterval={1800}
      />

      <div className="section">
        <div className="container-tight">
          {/* Progress */}
          <div className="mb-8">
            <ProgressBar current={3} total={3} />
          </div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="text-white" size={32} />
            </div>
            <h1 className="heading">Almost Done!</h1>
            <p className="subheading">
              Enter your email to receive your personalized productivity plan
            </p>
          </motion.div>

          {/* Error Display */}
          {rateLimitError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <RateLimitErrorDisplay error={rateLimitError} />
            </motion.div>
          )}

          {error && !rateLimitError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="error-message mb-6 flex items-start gap-3"
            >
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </motion.div>
          )}

          {/* Email Form */}
          <div className="card mb-6">
            <div className="mb-6">
              <label className="label">
                Email Address <span className="text-error-600">*</span>
              </label>
              <div className="relative">
                <Mail
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input pl-12"
                  autoFocus
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consentMarketing}
                  onChange={(e) => setConsentMarketing(e.target.checked)}
                  className="mt-1 w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-2 focus:ring-primary-200"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  Yes, send me productivity tips and advice from My Best Day (optional)
                </span>
              </label>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Your privacy matters:</strong> We'll email you your productivity plan and
                only send additional tips if you opt in. You can unsubscribe anytime.
              </p>
            </div>
          </div>

          {/* Turnstile Widget - Only show in production */}
          {!isLocalhost && (
            <div className="mb-6">
              <TurnstileWidget
                onSuccess={setTurnstileToken}
                onError={() => setError('Security verification failed. Please try again.')}
              />
            </div>
          )}

          {/* Development Mode Indicator */}
          {isLocalhost && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-900">
                <strong>Development Mode:</strong> Turnstile verification is bypassed for localhost testing.
              </p>
            </div>
          )}

          {/* What You'll Get */}
          <div className="card-simple mb-6 bg-gradient-to-br from-primary-50 to-blue-50">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              What You'll Receive:
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-primary-600 font-bold mt-0.5">✓</span>
                <span>Prioritized list of your goals with reasoning</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary-600 font-bold mt-0.5">✓</span>
                <span>Custom timeline with phases and milestones</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary-600 font-bold mt-0.5">✓</span>
                <span>Daily and weekly habits to accelerate progress</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary-600 font-bold mt-0.5">✓</span>
                <span>Immediate next actions for each goal</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary-600 font-bold mt-0.5">✓</span>
                <span>Personalized strategies and advice</span>
              </li>
            </ul>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => navigate('/questions')}
              className="btn-secondary"
              disabled={loading}
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="btn-primary btn-large flex-grow md:flex-grow-0"
            >
              {loading ? (
                <div className="flex items-center gap-3">
                  <LoadingSpinner inline />
                  <span>Generating Your Plan...</span>
                </div>
              ) : (
                <>
                  Generate My Productivity Plan
                  <Sparkles className="ml-2" size={20} />
                </>
              )}
            </button>
          </div>

          {/* Privacy Note */}
          <p className="text-xs text-gray-500 text-center mt-6">
            By continuing, you agree to our{' '}
            <a href="https://mybestday.ai/privacy" className="text-primary-600 hover:underline">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://mybestday.ai/terms" className="text-primary-600 hover:underline">
              Terms of Service
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
